import { useLayoutEffect, useState, type RefObject } from "react";
import { computeAnchoredPosition, type AnchoredPosition } from "./anchoredPosition";

/**
 * Tracks the viewport position a popover should take to stay attached to
 * `anchorRef`. Recomputed on open, window resize, scroll of ANY ancestor
 * (scroll events do not bubble, so the listener is registered in the capture
 * phase) AND on every animation frame while open, comparing the anchor's rect
 * with the last one and updating state only when it changed.
 *
 * panel-feedback-round3 T4: resize/scroll alone missed layout shifts that move
 * the anchor with neither event (a vertically centered modal growing when the
 * selected-company chips appear, a scrollbar narrowing the modal body), which
 * left the fixed popover behind. The placement (above/below) chosen at open
 * time is kept while it still fits, so a few pixels of drift never flip it.
 * Returns `null` while closed or before the anchor is measurable.
 */
export function useAnchoredPosition(
  anchorRef: RefObject<HTMLElement | null>,
  open: boolean,
): AnchoredPosition | null {
  const [position, setPosition] = useState<AnchoredPosition | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    let lastKey = "";
    let placement: AnchoredPosition["placement"] | undefined;
    const update = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      const key = [rect.left, rect.top, rect.bottom, rect.width, viewport.width, viewport.height].join("|");
      if (key === lastKey) return;
      lastKey = key;
      const next = computeAnchoredPosition(
        { left: rect.left, top: rect.top, bottom: rect.bottom, width: rect.width },
        viewport,
        { stickyPlacement: placement },
      );
      placement = next.placement;
      setPosition(next);
    };
    update();

    let frame = 0;
    const tick = () => {
      update();
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);

    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [anchorRef, open]);

  return position;
}
